import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@store/store.tsx';
import {
  selectPlanCapabilities,
  selectSiteApps,
  selectSiteFeatureFlags,
  selectSiteInfo,
} from '@store/slices/settingsSlice.tsx';
import {
  selectIsStaff,
  selectIsSuperuser,
  selectProfile,
  selectUserGroups,
  selectUserPermissions,
  selectUserRoles,
} from '@store/slices/userSlice.tsx';

import {
  DASHBOARD_NAV_ITEMS,
  findNavItemByPath,
  getVisibleNavItems,
} from '../registry/dashboardNavRegistry.ts';
import type { DashboardNavGroup, DashboardNavItem } from '../registry/types.ts';

const FAVORITES_STORAGE_VERSION = 1;
const MAX_RECENTS = 3;
const STORAGE_PREFIX = 'df_av.dashboard.shortcuts';
const EXCLUDED_SHORTCUT_KEYS = new Set(['dashboard.overview']);

interface StoredFavorites {
  version: number;
  keys: string[];
}

export interface DashboardShortcutItem {
  key: string;
  title: string;
  to: string;
  icon: DashboardNavItem['icon'];
  colorToken: string;
  group: DashboardNavGroup;
}

export interface DashboardRecentShortcut extends DashboardShortcutItem {
  visitedAt: number;
}

interface FavoritesState {
  loaded: boolean;
  keys: string[];
}

const isShortcutExcluded = (key: string) => EXCLUDED_SHORTCUT_KEYS.has(key);

const sanitizeSegment = (segment: string) =>
  segment
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-');

const buildStorageKey = (siteLabel: string, userLabel: string) =>
  `${STORAGE_PREFIX}::${sanitizeSegment(siteLabel || 'site')}::${sanitizeSegment(userLabel || 'guest')}`;

const toShortcut = (item: DashboardNavItem, to?: string): DashboardShortcutItem => ({
  key: item.key,
  title: item.title,
  to: to ?? item.to,
  icon: item.icon,
  colorToken: item.colorToken,
  group: item.group,
});

export const useDashboardShortcuts = () => {
  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const siteFeatures = useAppSelector(selectSiteFeatureFlags) ?? {};
  const planCapabilities = useAppSelector(selectPlanCapabilities);
  const siteInfo = useAppSelector(selectSiteInfo);
  const profile = useAppSelector(selectProfile);
  const isStaff = useAppSelector(selectIsStaff);
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const groups = useAppSelector(selectUserGroups) ?? [];
  const roles = useAppSelector(selectUserRoles) ?? [];
  const location = useLocation();

  const storageKey = useMemo(() => {
    const siteLabel = siteInfo?.name ?? 'site';
    const userLabel =
      profile?.id !== undefined && profile?.id !== null
        ? `user-${profile.id}`
        : profile?.username
            ? profile.username
            : profile?.email ?? 'guest';
    return buildStorageKey(siteLabel, userLabel);
  }, [profile?.email, profile?.id, profile?.username, siteInfo?.name]);

  const visibleItems = useMemo(() => {
    return getVisibleNavItems(DASHBOARD_NAV_ITEMS, {
      apps: siteApps,
      features: siteFeatures,
      isStaff,
      isSuperuser,
      permissions,
      groups,
      roles,
      planCapabilities,
    });
  }, [groups, isStaff, isSuperuser, permissions, planCapabilities, roles, siteApps, siteFeatures]);

  const navItemsByKey = useMemo(() => {
    return new Map<string, DashboardNavItem>(visibleItems.map((item) => [item.key, item]));
  }, [visibleItems]);

  const overviewShortcut = useMemo(() => {
    if (!isStaff) {
      return null;
    }

    const overviewItem = navItemsByKey.get('dashboard.overview');
    if (!overviewItem) {
      return null;
    }

    return toShortcut(overviewItem, '/dashboard/overview');
  }, [isStaff, navItemsByKey]);

  const [favoritesState, setFavoritesState] = useState<FavoritesState>(() => ({
    loaded: typeof window === 'undefined',
    keys: [],
  }));
  const [recents, setRecents] = useState<DashboardRecentShortcut[]>([]);

  const persistTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setFavoritesState({ loaded: true, keys: [] });
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey);

      if (!raw) {
        setFavoritesState({ loaded: true, keys: [] });
        return;
      }

      const parsed = JSON.parse(raw) as StoredFavorites | null;
      if (!parsed || parsed.version !== FAVORITES_STORAGE_VERSION || !Array.isArray(parsed.keys)) {
        setFavoritesState({ loaded: true, keys: [] });
        return;
      }

      setFavoritesState({
        loaded: true,
        keys: parsed.keys.filter((key) => typeof key === 'string' && !isShortcutExcluded(key)),
      });
    } catch {
      setFavoritesState({ loaded: true, keys: [] });
    }
  }, [storageKey]);

  useEffect(() => {
    if (!favoritesState.loaded) {
      return;
    }

    setFavoritesState((prev) => {
      if (!prev.loaded) {
        return prev;
      }

      const seen = new Set<string>();
      const normalizedKeys: string[] = [];

      const pushKey = (key: string) => {
        if (seen.has(key) || isShortcutExcluded(key) || !navItemsByKey.has(key)) {
          return;
        }

        seen.add(key);
        normalizedKeys.push(key);
      };

      prev.keys.forEach((storedKey) => {
        if (typeof storedKey !== 'string' || !storedKey) {
          return;
        }

        if (navItemsByKey.has(storedKey)) {
          pushKey(storedKey);
          return;
        }

        if (!storedKey.includes('/')) {
          return;
        }

        const [path, ...queryParts] = storedKey.split('?');
        const search = queryParts.length ? `?${queryParts.join('?')}` : undefined;
        const navItem = findNavItemByPath(visibleItems, path || '/', search);

        if (navItem) {
          pushKey(navItem.key);
        }
      });

      if (
        normalizedKeys.length === prev.keys.length &&
        normalizedKeys.every((key, index) => key === prev.keys[index])
      ) {
        return prev;
      }

      return { ...prev, keys: normalizedKeys };
    });
  }, [favoritesState.loaded, navItemsByKey, visibleItems]);

  useEffect(() => {
    if (!favoritesState.loaded || typeof window === 'undefined') {
      return;
    }

    const persist = () => {
      try {
        if (!favoritesState.keys.length) {
          window.localStorage.removeItem(storageKey);
        } else {
          const payload: StoredFavorites = {
            version: FAVORITES_STORAGE_VERSION,
            keys: favoritesState.keys,
          };
          window.localStorage.setItem(storageKey, JSON.stringify(payload));
        }
      } catch {
        /* no-op */
      }
    };

    if (persistTimeoutRef.current) {
      window.clearTimeout(persistTimeoutRef.current);
    }

    persistTimeoutRef.current = window.setTimeout(() => {
      persist();
      persistTimeoutRef.current = null;
    }, 200);

    return () => {
      if (persistTimeoutRef.current) {
        window.clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
        persist();
      }
    };
  }, [favoritesState.keys, favoritesState.loaded, storageKey]);

  useEffect(() => {
    const matched = findNavItemByPath(visibleItems, location.pathname, location.search);
    if (!matched || isShortcutExcluded(matched.key)) {
      return;
    }

    setRecents((prev) => {
      const next = prev.filter((item) => item.key !== matched.key);
      const target = `${location.pathname}${location.search ?? ''}` || matched.to;
      next.unshift({ ...toShortcut(matched, target || matched.to), visitedAt: Date.now() });
      return next.slice(0, MAX_RECENTS);
    });
  }, [location.pathname, location.search, visibleItems]);

  const toggleFavorite = useCallback(
    (key: string) => {
      if (!navItemsByKey.has(key) || isShortcutExcluded(key)) {
        return false;
      }

      let toggled = false;

      setFavoritesState((prev) => {
        if (!prev.loaded) {
          return prev;
        }

        if (prev.keys.includes(key)) {
          toggled = true;
          return { ...prev, keys: prev.keys.filter((storedKey) => storedKey !== key) };
        }

        toggled = true;
        return { ...prev, keys: [...prev.keys, key] };
      });

      return toggled;
    },
    [navItemsByKey],
  );

  const isFavorite = useCallback(
    (key: string) => favoritesState.loaded && favoritesState.keys.includes(key),
    [favoritesState.loaded, favoritesState.keys],
  );

  const favorites = useMemo(() => {
    if (!favoritesState.loaded) {
      return [] as DashboardShortcutItem[];
    }

    const definedItems = favoritesState.keys
      .map((key) => navItemsByKey.get(key))
      .filter((item): item is DashboardNavItem => Boolean(item));

    return definedItems.filter((item) => !isShortcutExcluded(item.key)).map((item) => toShortcut(item));
  }, [favoritesState.keys, favoritesState.loaded, navItemsByKey]);

  return {
    favorites,
    recents,
    toggleFavorite,
    isFavorite,
    loading: !favoritesState.loaded,
    overviewShortcut,
  } as const;
};

export type UseDashboardShortcutsReturn = ReturnType<typeof useDashboardShortcuts>;
